import { useState } from 'react';
import { Link } from 'react-router';

import { useComponents } from '@/api/queries';
import { useCan } from '@/auth/roles';
import { NotFoundPage } from '@/app/errors';
import { configPath, proposalPath, runsPath, useScopeParam } from '@/app/scope';
import { Button } from '@/components/Button';
import {
  BlindBadge,
  HealthBadge,
  OutcomeBadge,
  ProposalStatusBadge,
  Tag,
} from '@/components/badges';
import { ArrowRight, Warning } from '@/components/icons';
import { DIALOG_LINGER_MS, useLingering } from '@/components/motion';
import { ErrorNotice, Loading, Panel, Table, Td, Th } from '@/components/primitives';
import { Timestamp } from '@/components/Timestamp';
import { HEALTH_LABEL, groupOf } from '@/domain/health';
import type { ComponentView, Scope } from '@/domain/types';

import { ManualProposalDialog } from '../proposals/ManualProposalDialog';

/**
 * The component view, per scope (TDD §5.3): Observed · Implied · Published · Blind · Proposal.
 * The Implied → Published pair is the point of the screen, so a row whose evidence disagrees
 * with the page carries a coloured edge in the implied Health.
 */
export function ComponentsPage() {
  const scope = useScopeParam();
  if (!scope) return <NotFoundPage />;
  return <ComponentsTable scope={scope} />;
}

function ComponentsTable({ scope }: { scope: Scope }) {
  const query = useComponents(scope);
  const [changing, setChanging] = useState<ComponentView | null>(null);
  const dialogFor = useLingering(changing, DIALOG_LINGER_MS);

  if (query.isPending) return <Loading />;
  if (query.isError) {
    return (
      <ErrorNotice
        message={`Could not load the components: ${query.error.message}`}
        action={
          <Button size="sm" onClick={() => query.refetch()}>
            Try again
          </Button>
        }
      />
    );
  }

  const rows = query.data.components;
  const disagreeing = rows.filter(
    (c) =>
      c.state.implied_status !== c.state.published_status &&
      c.state.published_status !== 'maintenance',
  );
  const blind = rows.filter((c) => c.state.blind_since);
  const maintenance = rows.filter((c) => c.state.published_status === 'maintenance');

  const grouped: { group: string | null; items: ComponentView[] }[] = [];
  for (const row of rows) {
    const group = groupOf(row.config.capability_id);
    const last = grouped[grouped.length - 1];
    if (last && last.group === group && group !== null) last.items.push(row);
    else grouped.push({ group, items: [row] });
  }

  return (
    <div className="flex flex-col gap-4">
      <Panel bodyClassName="p-0">
        {/* The counts sit as the table's caption, inside the card. */}
        <p className="flex flex-wrap gap-x-4 gap-y-1 px-5 py-2.5 text-body text-ink-2">
          <span>{rows.length} components</span>
          {disagreeing.length > 0 ? <span>{disagreeing.length} disagree with the page</span> : null}
          {blind.length > 0 ? <span>{blind.length} blind</span> : null}
          {maintenance.length > 0 ? <span>{maintenance.length} under maintenance</span> : null}
        </p>
        <Table className="[&>tbody>tr:last-child>td]:border-b-0">
          <thead>
            <tr>
              <Th className="pl-5">Component</Th>
              <Th title="What the runner last said">Observed</Th>
              <Th title="What the ladder makes of the recent runs">Implied</Th>
              <Th title="What was last written to the page">Published</Th>
              <Th>Proposal</Th>
              <Th className="text-right pr-5">
                <span className="sr-only">Actions</span>
              </Th>
            </tr>
          </thead>
          <tbody>
            {grouped.map((block, i) => (
              <GroupBlock
                key={block.group ?? `flat-${i}`}
                block={block}
                scope={scope}
                onChange={setChanging}
              />
            ))}
          </tbody>
        </Table>
      </Panel>

      {dialogFor ? (
        <ManualProposalDialog
          key={dialogFor.config.capability_id}
          component={dialogFor}
          open={changing !== null}
          onClose={() => setChanging(null)}
        />
      ) : null}
    </div>
  );
}

function GroupBlock({
  block,
  scope,
  onChange,
}: {
  block: { group: string | null; items: ComponentView[] };
  scope: Scope;
  onChange: (view: ComponentView) => void;
}) {
  return (
    <>
      {block.group ? (
        <tr className="bg-surface-2">
          <th
            scope="rowgroup"
            colSpan={6}
            className="text-left px-5 py-1.5 text-meta font-medium text-ink-2 border-b border-line"
          >
            {block.group}
          </th>
        </tr>
      ) : null}
      {block.items.map((view) => (
        <ComponentRow
          key={view.config.capability_id}
          view={view}
          scope={scope}
          child={block.group !== null}
          onChange={onChange}
        />
      ))}
    </>
  );
}

const EDGE: Record<string, string> = {
  ok: 'border-l-status-ok',
  impaired: 'border-l-status-impaired',
  degraded: 'border-l-status-degraded',
  failed: 'border-l-status-failed',
  maintenance: 'border-l-status-maintenance',
};

function ComponentRow({
  view,
  scope,
  child,
  onChange,
}: {
  view: ComponentView;
  scope: Scope;
  child: boolean;
  onChange: (view: ComponentView) => void;
}) {
  const { state, config, proposal } = view;
  const canChange = useCan('approver');
  const disagrees =
    state.implied_status !== state.published_status && state.published_status !== 'maintenance';
  const streak =
    state.observed_outcome === 'FAIL' && state.fail_streak > 1
      ? `${state.fail_streak} failing in a row`
      : state.observed_outcome === 'PASS' && state.pass_streak > 1
        ? `${state.pass_streak} passing in a row`
        : null;

  return (
    <tr
      className={[
        'border-l-[3px] transition-tone hover:bg-surface-2',
        disagrees ? EDGE[state.implied_status] : 'border-l-transparent',
        disagrees ? 'bg-surface-2/60' : '',
      ].join(' ')}
    >
      <Td className={child ? 'pl-9' : 'pl-5'}>
        <div className="font-medium text-ink">{config.display_name}</div>
        <div className="text-meta text-ink-3 flex flex-wrap items-center gap-x-2">
          <code>{config.capability_id}</code>
          {state.blind_since ? (
            <span className="inline-flex items-center gap-1.5">
              <BlindBadge />
              <span>
                since <Timestamp iso={state.blind_since} mode="clock" />
                {state.last_observed_at ? (
                  <>
                    {' '}
                    · last run <Timestamp iso={state.last_observed_at} mode="clock" />
                  </>
                ) : (
                  ' · never reported'
                )}
              </span>
            </span>
          ) : null}
        </div>
      </Td>
      <Td>
        {state.observed_outcome && state.last_observed_at ? (
          <div className="flex flex-col gap-0.5">
            <span className="flex items-center gap-2">
              <OutcomeBadge outcome={state.observed_outcome} />
              <Timestamp iso={state.last_observed_at} mode="auto" className="text-ink-2" />
            </span>
            <span className="text-meta text-ink-3">
              {state.observed_outcome === 'FAIL' && state.driving_check ? (
                <>
                  <code>{state.driving_check}</code>
                  {streak ? (
                    <>
                      {' · '}
                      <span className="whitespace-nowrap">{streak}</span>
                    </>
                  ) : null}
                </>
              ) : streak ? (
                <span className="whitespace-nowrap">{streak}</span>
              ) : null}
            </span>
          </div>
        ) : (
          <span className="text-ink-3">No runs yet</span>
        )}
      </Td>
      <Td>
        <HealthBadge health={state.implied_status} variant="text" size="sm" />
      </Td>
      <Td>
        <span className="inline-flex items-center gap-2">
          <HealthBadge health={state.published_status} />
          {state.publish_uncertain ? (
            <Tag
              tone="warning"
              icon={<Warning size={12} />}
              title="The last write to the page got no response; it may have landed."
            >
              Unconfirmed
            </Tag>
          ) : null}
        </span>
      </Td>
      <Td>
        {proposal ? (
          <Link
            to={proposalPath(proposal.proposal_id)}
            className="inline-flex flex-col gap-0.5 no-underline hover:no-underline group"
          >
            <span className="flex items-center gap-1.5">
              <ProposalStatusBadge status={proposal.status} />
              {proposal.status === 'publish_failed' ? (
                <span className="text-meta text-status-failed-ink font-medium whitespace-nowrap">
                  Retry now
                </span>
              ) : null}
            </span>
            <span className="text-meta text-ink-2 group-hover:underline inline-flex items-center gap-1">
              to {HEALTH_LABEL[proposal.to_status]} <ArrowRight size={12} />
            </span>
          </Link>
        ) : (
          <span className="text-ink-3">—</span>
        )}
      </Td>
      <Td className="text-right pr-5 whitespace-nowrap">
        {/* Quiet links and a ghost button: the status columns lead, the controls wait. */}
        <div className="inline-flex items-center gap-1">
          <Link to={runsPath(scope, config.capability_id)} className="px-1.5">
            Runs
          </Link>
          <Link to={configPath(scope, config.capability_id)} className="px-1.5">
            Configure
          </Link>
          {canChange ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onChange(view)}
              disabled={proposal?.status === 'approved'}
            >
              Change…
            </Button>
          ) : null}
        </div>
      </Td>
    </tr>
  );
}
